import React, { useEffect, useState } from "react";
import {
    Accordion,
    AccordionDetails,
    AccordionSummary,
    Grid,
    TextField,
    LinearProgress,
    MenuItem,
    Pagination,
    Select,
    Typography,
    Box,
    FormControl,
    InputLabel,
    Chip,
    Button,
} from "@mui/material";
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ClearIcon from '@mui/icons-material/Clear';
import { useBiddingController } from "./context/bidding-controller";
import { useBiddingState } from "./context/bidding-context";
import { IListBiddingRequest } from "../../model/base/request/list-bidding-request";
import PageHeader from "../components/shared/page-header";
import dayjs from "dayjs";
import { useNavigate } from "../../hooks/useNavigate";
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import VerifiedIcon from '@mui/icons-material/Verified';
import FilterPanel, { ResultsPanel } from '../components/shared/filter-panel';
import EmptyState, { hasActiveFilters } from '../components/shared/empty-state';
import ListSkeleton from '../components/shared/list-skeleton';

export function BiddingComponent() {
    const [initialLoading, setInitialLoading] = useState(true);
    const [loadMoreData, setLoadMoreData] = useState(false);
    const { loadData } = useBiddingController();
    const { biddings, modalities, status } = useBiddingState();
    const [paramsRequet, setParamsRequest] = useState<IListBiddingRequest>({ page: 0, pageSize: 100 } as IListBiddingRequest);
    const navigate = useNavigate();


    useEffect(() => {
        const searchData = async () => {
            setLoadMoreData(true);
            await loadData(paramsRequet);
            setLoadMoreData(false);
            setInitialLoading(false);
        };
        searchData();
    }, [paramsRequet]);

    const handleClearFilters = () => {
        setParamsRequest({ page: 0, pageSize: 100 } as IListBiddingRequest);
    };

    const handleViewDetails = (element: any) => {
        if (element?.externalUrl) {
            window.open(element.externalUrl, '_blank', 'noopener,noreferrer');
        } else {
            navigate(`/licitacoes/${element?.id}`);
        }
    };

    return (
        <Box>
            <PageHeader
                title="Licitações"
                subtitle="Consulte todas as licitações do município"
                breadcrumbs={[{ label: 'Licitações' }]}
            />

            {initialLoading ? (
                <ListSkeleton />
            ) : (
                <React.Fragment>
                    {/* Filtros */}
                    <FilterPanel>
                        <Grid container spacing={2} alignItems="center">
                            <Grid item xs={12} sm={6} md={4}>
                                <TextField
                                    fullWidth
                                    label="Buscar por descrição"
                                    placeholder="Digite para buscar na descrição"
                                    value={paramsRequet.term || ''}
                                    onChange={(e) => {
                                        setParamsRequest({ ...paramsRequet, page: 0, term: e.target.value });
                                    }}
                                />
                            </Grid>
                            <Grid item xs={12} sm={6} md={2.5}>
                                <FormControl fullWidth>
                                    <InputLabel>Status</InputLabel>
                                    <Select
                                        value={paramsRequet.statusId || ''}
                                        label="Status"
                                        onChange={(e) =>
                                            setParamsRequest({
                                                ...paramsRequet,
                                                page: 0,
                                                statusId: e.target.value as number,
                                            })
                                        }
                                    >
                                        <MenuItem value="">
                                            <em>Todos</em>
                                        </MenuItem>
                                        {status?.map((element) => (
                                            <MenuItem value={element.id} key={element.id}>
                                                {element.name}
                                            </MenuItem>
                                        ))}
                                    </Select>
                                </FormControl>
                            </Grid>
                            <Grid item xs={12} sm={6} md={2.5}>
                                <FormControl fullWidth>
                                    <InputLabel>Modalidade</InputLabel>
                                    <Select
                                        value={paramsRequet.modalityId || ''}
                                        label="Modalidade"
                                        onChange={(e) =>
                                            setParamsRequest({
                                                ...paramsRequet,
                                                page: 0,
                                                modalityId: e.target.value as number,
                                            })
                                        }
                                    >
                                        <MenuItem value="">
                                            <em>Todas</em>
                                        </MenuItem>
                                        {modalities?.map((element) => (
                                            <MenuItem value={element.id} key={element.id}>
                                                {element.name}
                                            </MenuItem>
                                        ))}
                                    </Select>
                                </FormControl>
                            </Grid>
                            <Grid item xs={12} sm={6} md={3}>
                                <Button
                                    variant="outlined"
                                    color="primary"
                                    startIcon={<ClearIcon />}
                                    onClick={handleClearFilters}
                                    fullWidth
                                    sx={{ height: '56px' }}
                                >
                                    Limpar Filtros
                                </Button>
                            </Grid>
                        </Grid>
                    </FilterPanel>

                    {/* Resultados */}
                    <ResultsPanel>
                        {loadMoreData && <LinearProgress sx={{ mb: 2 }} />}

                        {biddings?.items && biddings.items.length > 0 ? (
                            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                                {biddings.items.map((element, index) => (
                                    <Accordion key={index} defaultExpanded={false}>
                                        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                                            <Box sx={{ display: 'flex', justifyContent: 'space-between', width: '100%', pr: 2, alignItems: 'center', gap: 1 }}>
                                                <Typography fontWeight={600} sx={{ flex: 1 }}>
                                                    {element?.number}
                                                </Typography>
                                                <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                                                    {element?.isPncp && (
                                                        <Chip
                                                            icon={<VerifiedIcon />}
                                                            label="PNCP"
                                                            size="small"
                                                            color="success"
                                                            variant="outlined"
                                                        />
                                                    )}
                                                    <Chip
                                                        label={element?.modality}
                                                        size="small"
                                                        color="primary"
                                                        variant="outlined"
                                                    />
                                                </Box>
                                            </Box>
                                        </AccordionSummary>
                                        <AccordionDetails>
                                            <Grid container spacing={2}>
                                                <Grid item xs={12} sm={6}>
                                                    <Typography variant="body2" color="text.secondary">
                                                        <strong>Modalidade:</strong> {element?.modality}
                                                    </Typography>
                                                </Grid>
                                                <Grid item xs={12} sm={6}>
                                                    <Typography variant="body2" color="text.secondary">
                                                        <strong>Tipo:</strong> {element?.statusDescripton || element?.status || '-'}
                                                    </Typography>
                                                </Grid>
                                                <Grid item xs={12} sm={6}>
                                                    <Typography variant="body2" color="text.secondary">
                                                        <strong>Unidade:</strong> {element?.managementUnit}
                                                    </Typography>
                                                </Grid>
                                                <Grid item xs={12} sm={6}>
                                                    <Typography variant="body2" color="text.secondary">
                                                        <strong>Data Limite:</strong>{' '}
                                                        {element?.limitDate
                                                            ? dayjs(element.limitDate).format('DD/MM/YYYY')
                                                            : '-'}
                                                    </Typography>
                                                </Grid>
                                                <Grid item xs={12} sm={6}>
                                                    <Typography variant="body2" color="text.secondary">
                                                        <strong>Data de Abertura:</strong>{' '}
                                                        {element?.openedOn
                                                            ? dayjs(element.openedOn).format('DD/MM/YYYY')
                                                            : '-'}
                                                    </Typography>
                                                </Grid>
                                                <Grid item xs={12} sm={6}>
                                                    <Typography variant="body2" color="text.secondary">
                                                        <strong>Valor Homologado:</strong> {element?.formattedValue || '-'}
                                                    </Typography>
                                                </Grid>
                                                <Grid item xs={12}>
                                                    <Typography variant="body2" color="text.secondary">
                                                        <strong>Objeto:</strong> {element?.object}
                                                    </Typography>
                                                </Grid>
                                                <Grid item xs={12}>
                                                    <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
                                                        <Button
                                                            variant="text"
                                                            endIcon={element?.externalUrl ? <OpenInNewIcon /> : <ArrowForwardIcon />}
                                                            onClick={() => handleViewDetails(element)}
                                                            size="small"
                                                            sx={{
                                                                textTransform: 'none',
                                                                fontWeight: 500
                                                            }}
                                                        >
                                                            {element?.externalUrl ? 'Ver no site externo' : 'Ver detalhes'}
                                                        </Button>
                                                    </Box>
                                                </Grid>
                                            </Grid>
                                        </AccordionDetails>
                                    </Accordion>
                                ))}
                            </Box>
                        ) : (
                            <EmptyState title="Nenhuma licitação encontrada" filtered={hasActiveFilters(paramsRequet)} onClearFilters={handleClearFilters} />
                        )}

                        {biddings?.pageCount > 0 && (
                            <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}>
                                <Pagination
                                    count={biddings.pageCount}
                                    page={(paramsRequet.page || 0) + 1}
                                    onChange={(_, page) => {
                                        setParamsRequest({ ...paramsRequet, page: page - 1 });
                                    }}
                                    disabled={loadMoreData}
                                    color="primary"
                                    showFirstButton
                                    showLastButton
                                    siblingCount={1}
                                    boundaryCount={1}
                                />
                            </Box>
                        )}
                    </ResultsPanel>
                </React.Fragment>
            )}
        </Box>
    );
}
